import i18n from 'i18next'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { useAuth as useOidcAuth } from 'react-oidc-context'
import { toast } from 'sonner'
import { AUTH_PROVIDER } from '@/config'
import { apiClient, authApi, type RegisterRequest, type User } from '@/services/api'
import { resolveLanguage } from '@/services/i18n/languages'

export interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (username: string, password: string) => Promise<void>
  register: (data: RegisterRequest) => Promise<User>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

/** Exported for stories, which stand in for the provider: the app itself mounts `AuthProvider`. */
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Server is authoritative on language: when the user record arrives (or
// changes), reconcile i18n with the persisted preference. Compare on the base
// language code so `cs-CZ` ↔ `cs` matches.
function useReconcileLanguage(user: User | null) {
  useEffect(() => {
    if (!user?.preferred_language) return
    const current = resolveLanguage(i18n.language)
    if (current !== user.preferred_language) {
      i18n.changeLanguage(user.preferred_language)
    }
  }, [user?.preferred_language])
}

// Clear local session state when the API client signals an unrecoverable 401.
function useUnauthorizedListener(setUser: (user: User | null) => void) {
  useEffect(() => {
    const handler = () => {
      setUser(null)
      apiClient.clearToken()
      toast.error('Session expired. Please login again.')
    }
    window.addEventListener('auth:unauthorized', handler)
    return () => window.removeEventListener('auth:unauthorized', handler)
  }, [setUser])
}

// Surface a transient auth-service outage WITHOUT clearing the session — the
// access token is likely still valid and the client keeps retrying.
function useConnectionLostListener() {
  useEffect(() => {
    const handler = () => toast.error('Connection lost — retrying. You stay signed in.')
    window.addEventListener('auth:connection-lost', handler)
    return () => window.removeEventListener('auth:connection-lost', handler)
  }, [])
}

// --- Password (built-in username/password) -----------------------------------

function PasswordAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (apiClient.getToken()) {
          setUser(await authApi.me())
        } else {
          // No token (e.g. new tab) - try to refresh using the httpOnly cookie.
          const refreshed = await apiClient.refreshToken()
          if (refreshed) setUser(await authApi.me())
        }
      } catch (_error) {
        apiClient.clearToken()
      } finally {
        setIsLoading(false)
      }
    }
    initAuth()
  }, [])

  useReconcileLanguage(user)
  useUnauthorizedListener(setUser)
  useConnectionLostListener()

  const login = async (username: string, password: string) => {
    try {
      const { access_token } = await authApi.login({ username, password })
      apiClient.setToken(access_token)
      const currentUser = await authApi.me()
      setUser(currentUser)
      toast.success(`Welcome back, ${currentUser.username}!`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Login failed')
      throw error
    }
  }

  const register = async (data: RegisterRequest) => {
    try {
      const newUser = await authApi.register(data)
      toast.success('Registration successful! Please login.')
      return newUser
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Registration failed')
      throw error
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      apiClient.clearToken()
      toast.success('Logged out successfully')
    }
  }

  const refreshUser = async () => {
    try {
      setUser(await authApi.me())
    } catch (error) {
      console.error('Failed to refresh user:', error)
      setUser(null)
      apiClient.clearToken()
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// --- OIDC (Keycloak via react-oidc-context) ----------------------------------

function OidcBackedAuthProvider({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth()
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(false)

  // Feed the OIDC access token into the shared apiClient slot, then resolve the
  // local user record. Re-runs whenever the token changes (login / silent renew).
  const accessToken = oidc.user?.access_token
  useEffect(() => {
    if (!oidc.isAuthenticated || !accessToken) {
      setUser(null)
      return
    }
    apiClient.setToken(accessToken)
    setUserLoading(true)
    let cancelled = false
    authApi
      .me()
      .then(u => {
        if (!cancelled) setUser(u)
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null)
          apiClient.clearToken()
        }
      })
      .finally(() => {
        if (!cancelled) setUserLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [oidc.isAuthenticated, accessToken])

  useReconcileLanguage(user)
  useUnauthorizedListener(setUser)
  useConnectionLostListener()

  const login = async (_username: string, _password: string) => {
    await oidc.signinRedirect()
  }

  const register = async (_data: RegisterRequest): Promise<User> => {
    toast.error('Registration is invite-only')
    throw new Error('Self-service registration is disabled')
  }

  const logout = async () => {
    apiClient.clearToken()
    setUser(null)
    try {
      await oidc.signoutRedirect()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const refreshUser = async () => {
    try {
      setUser(await authApi.me())
    } catch (error) {
      console.error('Failed to refresh user:', error)
      setUser(null)
    }
  }

  const value: AuthContextType = {
    user,
    isLoading: oidc.isLoading || userLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return AUTH_PROVIDER === 'oidc' ? (
    <OidcBackedAuthProvider>{children}</OidcBackedAuthProvider>
  ) : (
    <PasswordAuthProvider>{children}</PasswordAuthProvider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
